import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import i18n from '../../../../localization/i18n';
import * as rk from '../../../../localization/resourceKeys';

interface CaptchaGateProps {
  onStatusChange: (isValid: boolean) => void;
  onTokenChange?: (token: string | null) => void;
  resetSignal?: number;
  widgetTestId?: string;
  errorTestId?: string;
}

const siteKey = '6LcAP-0bAAAAAI2QfcaulKzivfpCv3symKFQ1S22';

const CaptchaGate: React.FC<CaptchaGateProps> = ({
  onStatusChange,
  onTokenChange,
  resetSignal,
  widgetTestId = 'myvw-captcha-widget',
  errorTestId = 'myvw-captcha-error',
}) => {
  const captchaRef = useRef<any>(null);
  const [captchaError, setCaptchaError] = useState(false);

  const isCaptchaEnabled = useMemo(
    () => process.env.REACT_APP_AWS_ENV !== 'dev' && process.env.NODE_ENV !== 'test',
    [],
  );

  useEffect(() => {
    if (!isCaptchaEnabled) {
      onStatusChange(true);
      if (onTokenChange) onTokenChange(null);
    }
  }, [isCaptchaEnabled, onStatusChange, onTokenChange]);

  useEffect(() => {
    if (!isCaptchaEnabled || typeof resetSignal === 'undefined') return;

    captchaRef?.current?.reset();
    setCaptchaError(false);
    onStatusChange(false);
    if (onTokenChange) onTokenChange(null);
  }, [isCaptchaEnabled, onStatusChange, onTokenChange, resetSignal]);

  const handleCaptchaSuccess = (token: string | null) => {
    setCaptchaError(false);
    onStatusChange(!!token);
    if (onTokenChange) onTokenChange(token);
  };

  const handleCaptchaError = () => {
    onStatusChange(false);
    if (onTokenChange) onTokenChange(null);
  };

  const handleCaptchaExpired = () => {
    setCaptchaError(true);
    onStatusChange(false);
    if (onTokenChange) onTokenChange(null);
  };

  if (!isCaptchaEnabled) return null;

  return (
    <>
      <div data-testid={widgetTestId}>
        <ReCAPTCHA
          sitekey={siteKey}
          ref={captchaRef}
          onChange={handleCaptchaSuccess}
          onExpired={handleCaptchaExpired}
          onErrored={handleCaptchaError}
        />
      </div>
      {captchaError && (
        <p className="c-error-message u-mt" data-testid={errorTestId}>
          {i18n.t(rk.ERROR_CAPTCHA_EXPIRED)}
        </p>
      )}
    </>
  );
};

export default CaptchaGate;
